import type { CSSProperties, ReactNode } from "react";
import { BackstageIcon } from "../shared/BackstageIcon";
import styles from "./EntitlementDetailPanel.module.css";

/**
 * The one detail panel layout for a group, a Division and a Department: a title row (the name and the Backstage icon when imported),
 * then a two-column grid of rows. Every kind renders the same rows in the same order, so the layout does not move when the selection
 * changes. A row that does not apply to the selected kind is still rendered, with a lighter label and an empty value column.
 */
export function EntitlementDetailPanel({ headingId, heading, backstageName, style, children, footer }: {
  headingId: string;
  heading: ReactNode;
  backstageName?: string | null;
  style?: CSSProperties;
  children: ReactNode;
  footer?: ReactNode;
}): React.JSX.Element {
  return (
    <section aria-labelledby={headingId} className={styles.panel} style={style}>
      <div className={styles.titleRow}>
        {heading}
        {backstageName ? <BackstageIcon slug={backstageName} /> : null}
      </div>
      <dl className={styles.rows}>{children}</dl>
      {footer}
    </section>
  );
}

/**
 * One row of a detail panel: a label in the left column and its value in the right. When the row does not apply, the value is not
 * rendered at all, so no control or text can appear in it.
 */
export function DetailRow({ label, applies = true, children }: {
  label: ReactNode;
  applies?: boolean;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <div className={applies ? styles.row : `${styles.row} ${styles.rowNotApplicable}`}>
      <dt>{label}</dt>
      <dd>{applies ? children : null}</dd>
    </div>
  );
}
